import type { FC } from "react";
import React, { useEffect, useMemo } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import { BLOOD_SPOT_SKU } from "src/constants/misc";
import Path from "src/constants/path";
import PushNotificationsBloc from "src/hybrid/components/PushNotificationsControls";
import { createAppPath, preserveQueryParams } from "src/lib/createAppPath";
import SignupCubit from "src/state/SignupCubit/SignupCubit";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { useBlocNext } from "@blac/react";
import { useBloc } from "src/state/state";
import AnimatedPage from "src/ui/components/AnimatedPage/AnimatedPage";
import AppPageProvider from "src/ui/components/AppPageProvider/AppPageProvider";
import AnnouncementCardDialog from "src/ui/components/AppQueryPopups/dialogs/AnnouncementCardDialog";
import AppStatusBar from "src/ui/components/AppStatusBar/AppStatusBar";
import CustomSuspense from "src/ui/components/CustomSuspense/CustomSuspense";
import LabResultsDataSubPageRouter from "src/ui/components/LabResultsDataSubPageRouter/LabResultsDataSubPageRouter";
import ReadMore from "src/ui/components/ReadMore/ReadMore";
import { Prompts } from "src/ui/pages/AppPage/components/Prompts/Prompts";
import HomePage from "src/ui/pages/HomePage/HomePage";
import AppQueryPopupsRedirects from "../AppQueryPopups/AppQueryPopupsRedirects";
import InAppQuestionnaire from "../InAppQuestionnaire/InAppQuestionnaire";
import MealInsightOverview from "../MealInsights/MealInsightOverview";
import RedirectWithQueryParams from "../RedirectWithQueryParams/RedirectWithQueryParams";
import MealsPage from "src/ui/pages/MealsPage/MealsPage";
import LegacyRedirects, { resolveLegacyRedirectPath } from "./LegacyRedirects";
import { FeatureFlags } from "src/lib/featureFlags";
import LeapChatRouter from "../LeapChat/LeapChatRouter";
import AppNotificationsRedirect from "../AppNotificationsRedirect/AppNotificationsRedirect";

const Async = {
  CustomerIo: React.lazy(
    () => import("src/ui/components/CustomerIo/CustomerIo")
  ),
  LearnPage: React.lazy(() => import("src/ui/pages/LearnPage/LearnPage"))
};

export const DisableAppPadding = () => (
  <div className="disable-app-padding" style={{ display: "none" }} />
);

const AppRouter: FC = () => {
  const [userPreferences, { loadUserPreferences }] =
    useBlocNext(UserPreferencesCubit);
  const [, { loadDynamicFlags, loadBackendFlags }] = useBloc(FeatureFlags);

  const navigate = useNavigate();
  const location = useLocation();

  const [, { setNavigateMethod }] = useBlocNext(PushNotificationsBloc);

  setNavigateMethod(navigate);

  const onboardingDone = useMemo(
    () => userPreferences[UserPreferenceKeys.onboardingDone],
    [userPreferences]
  );
  const legacyRedirectPath = resolveLegacyRedirectPath({
    pathname: location.pathname,
    search: location.search
  });

  useEffect(() => {
    void loadUserPreferences();
    void loadDynamicFlags();
    void loadBackendFlags();
    // remove zendesk widget
    SignupCubit.removeSupportChat();
  }, []);

  useEffect(() => {
    const ionContent = document.querySelector("ion-content");
    const scrollable = ionContent?.shadowRoot?.querySelector(".inner-scroll");
    scrollable?.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // clear onboarding data and redirect to '/app/messenger' from '/app'
  useEffect(() => {
    const target = createAppPath(Path.home);

    if (
      (location.pathname as Path) === Path.root ||
      location.pathname === `/${Path.app}`
    ) {
      const targetWithQuery = preserveQueryParams(target, location.search);
      navigate(targetWithQuery);
    }
  }, [onboardingDone]);

  if (legacyRedirectPath) {
    return <Navigate to={legacyRedirectPath} replace />;
  }

  return (
    <>
      <AppQueryPopupsRedirects />
      <AnnouncementCardDialog />
      <AppPageProvider>
        <CustomSuspense>
          <Async.CustomerIo />
        </CustomSuspense>
        <Prompts />
        <Routes>
          {/* redirect old "/messages" to new "/chat" */}
          <Route
            path={Path.messages}
            element={<RedirectWithQueryParams to={createAppPath(Path.chat)} />}
          />

          <Route
            path={`${Path.chat}/*`}
            element={
              <AnimatedPage data-testid="messages-page" flex disableSafeAreaTop>
                <LeapChatRouter />
              </AnimatedPage>
            }
          />

          <Route
            path={Path.home}
            element={
              <AnimatedPage data-testid="home-page">
                <HomePage />
              </AnimatedPage>
            }
          />

          <Route
            path={Path.profile}
            element={
              <AnimatedPage data-testid="profile-page">
                <HomePage showProfile />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.profile}/*`}
            element={
              <AnimatedPage data-testid="profile-router-page">
                <HomePage showProfile />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.signupQuestionnaire}/:id`}
            element={
              <AnimatedPage
                data-testid="in-app-questionnaire-page"
                spaceY="both"
              >
                <InAppQuestionnaire />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.journey}/:module?/:task?`}
            element={
              <AnimatedPage data-testid="learn-page">
                <Async.LearnPage />
              </AnimatedPage>
            }
          />

          {/* Redirect old routes to journey */}
          <Route path={Path.program} element={<LegacyRedirects />} />
          <Route
            path={`${Path.myPlan}/journey`}
            element={<LegacyRedirects />}
          />
          <Route
            path={`${Path.myPlan}/path-to-healthy-weight`}
            element={<LegacyRedirects />}
          />
          <Route
            path={`${Path.myPlan}/program/cdcdpp`}
            element={<LegacyRedirects />}
          />
          <Route
            path={Path.myPlan}
            element={<RedirectWithQueryParams to={createAppPath(Path.home)} />}
          />

          <Route
            path={`${Path.mealInsights}/:id`}
            element={
              <AnimatedPage
                id="meal-insights"
                data-testid="meal-insights-page"
                disableSafeAreaHorizontal
                disableSafeAreaBottom
              >
                <MealInsightOverview />
              </AnimatedPage>
            }
          />

          <Route path={Path.meals} element={<MealsPage />} />

          <Route
            path={`${Path.labResults}/*`}
            element={
              <AnimatedPage
                data-testid="lab-results-page"
                spaceY="bottom"
                disableSafeAreaTop
              >
                <LabResultsDataSubPageRouter />
              </AnimatedPage>
            }
          />

          <Route
            path="*"
            element={<RedirectWithQueryParams to={Path.home} />}
          />
        </Routes>
      </AppPageProvider>
      <AppStatusBar />
      <AppNotificationsRedirect />
      <ReadMore
        hidden
        name="labinstructions"
        slug={BLOOD_SPOT_SKU}
        fetchFromCms={false}
      />
    </>
  );
};

export default AppRouter;
